import React, { useEffect, useState, type FC, useContext } from 'react';
import Header from '../../components/header/header';
import styles from './editRoles.module.scss';
import DetailsViewProvider from '../../context/DetailsViewProvider';
import TabMenuAdmin from '../../components/tabMenuAdmin/tabMenuAdmin';
import ObrasAPI from '../../services/APIObras';
import { t } from 'i18next';
import SimpleSelect from '../fullDetail/components/simpleSelect/simpleSelect';
import { RemoteMasterOption, SelectOption } from '../../types/Option';
import RemotePaginatedSelect from './remotePaginatedSelect';
import { Button, Dialog, Stack, SvgIcon } from '@mui/material';
import { AdminContext } from '../../context/AdminProvider';
import LoadingSpinner from '../../components/loadingSpinner/loadingSpinner';
import { ReactComponent as error } from '../../assets/error.svg';
import { Usuario, UsuarioProyecto } from '../../types/Admin';
import { ADMIN, DOWNLOAD, EDITOR, VALIDATOR, VIEWER, VIEWER_VIP } from '../../userRoles/userRoles';



// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface EditRolesProps {
  hasProject: boolean,
}

interface RoleSelectOption extends SelectOption {
  role: string;
  scoped: boolean;
}

export const renderRole = (role?: string) => {
  switch (role) {
    case 'viewer':
      return t('administration.table.viewer');
    case 'viewer_vip':
      return t('administration.table.vip');
    case 'editor':
      return t('administration.table.editor');
    case 'validator':
      return t('administration.table.validator');
    case 'administrator':
      return t('administration.table.admin');
    case 'download':
      return t('administration.table.download');
  }
};
/* eslint-disable @typescript-eslint/no-non-null-assertion */
const EditRoles: FC<EditRolesProps> = (props) => {
  const { hasProject } = props;
  const api = ObrasAPI.getInstance();
  const [editable, setEditable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [initializationError, setInitializationError] = useState(false);
  const [initializationAttempt, setInitializationAttempt] = useState(0);
  const [openError, setOpenError] = useState(false);
  const [editableSupercenter, setEditableSupercenter] = useState(false);
  const [editableCenter, setEditableCenter] = useState(false);
  const allOptions = [t('administration.edition.allZones'), t('administration.edition.allSupercenters'), t('administration.edition.allProjects')];
  const selectAllOptions = allOptions.map((nombre, id) => { return { id, nombre }; });
  const adminContext = useContext(AdminContext);

  const selectOptionsRoles: RoleSelectOption[] = [
    { id: 1, nombre: t('administration.table.editor'), role: EDITOR, scoped: true },
    { id: 2, nombre: t('administration.table.validator'), role: VALIDATOR, scoped: true },
    { id: 3, nombre: t('administration.table.download'), role: DOWNLOAD, scoped: false },
    { id: 4, nombre: t('administration.table.admin'), role: ADMIN, scoped: false },
    { id: 5, nombre: t('administration.table.viewer'), role: VIEWER, scoped: false },
    { id: 6, nombre: t('administration.table.vip'), role: VIEWER_VIP, scoped: true },
  ];
  const rol = renderRole(adminContext.permissionToEdit?.rol);
  const defaultRole = (hasProject && rol) ? selectOptionsRoles.find((option) => option.nombre === rol) : selectOptionsRoles[0];
  const [role, setRole] = useState(defaultRole);

  const optionsEnterprise = ['ACCIONA Construcción'];
  const selectOptionsEnterprise = optionsEnterprise.map((nombre, id) => { return { id, nombre }; });

  const [optionsZone, setOptionsZone] = React.useState<RemoteMasterOption[]>([]);
  const [selectedZone, setSelectedZone] = React.useState<RemoteMasterOption | null>(null);
  const [optionsSupercenter, setOptionsSupercenter] = React.useState<RemoteMasterOption[]>([]);
  const [selectedSupercenter, setSelectedSupercenter] = React.useState<RemoteMasterOption | null>(null);
  const [optionsCenter, setOptionsCenter] = React.useState<RemoteMasterOption[]>([]);
  const [selectedCenter, setSelectedCenter] = React.useState<RemoteMasterOption | null>(null);

  const [zoneSearch, setZoneSearch] = useState('');
  const [supercenterSearch, setSupercenterSearch] = useState('');
  const [centerSearch, setCenterSearch] = useState('');
  const [zonePage, setZonePage] = useState(0);
  const [supercenterPage, setSupercenterPage] = useState(0);
  const [centerPage, setCenterPage] = useState(0);
  const [zoneHasNextPage, setZoneHasNextPage] = useState(false);
  const [supercenterHasNextPage, setSupercenterHasNextPage] = useState(false);
  const [centerHasNextPage, setCenterHasNextPage] = useState(false);
  const [zoneLoading, setZoneLoading] = useState(true);
  const [supercenterLoading, setSupercenterLoading] = useState(false);
  const [centerLoading, setCenterLoading] = useState(false);

  const zoneRequest = React.useRef(0);
  const supercenterRequest = React.useRef(0);
  const centerRequest = React.useRef(0);
  const centerBusy = React.useRef(false);
  const centerRetry = React.useRef<(() => Promise<void>) | null>(null);
  const [centerError, setCenterError] = useState(false);
  const searchTimers = React.useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const mergeOptions = (current: RemoteMasterOption[], next: RemoteMasterOption[]) => {
    const merged = new Map(current.map((option) => [option.code, option]));
    next.forEach((option) => merged.set(option.code, option));
    return Array.from(merged.values());
  };

  const loadZones = async (search: string, page: number, append: boolean) => {
    const requestId = ++zoneRequest.current;
    setZoneLoading(true);
    try {
      const response = await api.getZonesPage(search, page);
      if (requestId !== zoneRequest.current) return;
      setOptionsZone((current) => append ? mergeOptions(current, response.results) : response.results);
      setZonePage(response.page);
      setZoneHasNextPage(response.hasNextPage);
    } finally {
      if (requestId === zoneRequest.current) setZoneLoading(false);
    }
  };

  const loadSupercenters = async (zoneCode: string, search: string, page: number, append: boolean) => {
    const requestId = ++supercenterRequest.current;
    setSupercenterLoading(true);
    try {
      const response = await api.getSupercentersPage(zoneCode, search, page);
      if (requestId !== supercenterRequest.current) return;
      setOptionsSupercenter((current) => append ? mergeOptions(current, response.results) : response.results);
      setSupercenterPage(response.page);
      setSupercenterHasNextPage(response.hasNextPage);
    } finally {
      if (requestId === supercenterRequest.current) setSupercenterLoading(false);
    }
  };

  const invalidateCenters = () => {
    centerRequest.current += 1;
    centerBusy.current = false;
    centerRetry.current = null;
    setCenterLoading(false);
    setCenterError(false);
  };

  const loadCenters = async (
    supercenterCode: string, search: string, page: number, append: boolean, selectedCode?: string,
  ) => {
    const requestId = ++centerRequest.current;
    centerBusy.current = true;
    setCenterLoading(true);
    setCenterError(false);
    centerRetry.current = () => loadCenters(supercenterCode, search, page, append, selectedCode);
    try {
      const response = await api.getCentersPage(supercenterCode, search, page);
      if (requestId !== centerRequest.current) return;
      let centers = response.results;
      if (selectedCode && !centers.some(center => center.code === selectedCode)) {
        const selectedPage = await api.getCentersPage(supercenterCode, selectedCode, 0);
        if (requestId !== centerRequest.current) return;
        centers = mergeOptions(centers, selectedPage.results);
      }
      setOptionsCenter(current => append ? mergeOptions(current, centers) : centers);
      setCenterPage(response.page);
      setCenterHasNextPage(response.hasNextPage);
      if (selectedCode) {
        const selected = centers.find(center => center.code === selectedCode) || null;
        if (!selected) throw new Error('Project center was not found');
        setSelectedCenter(selected);
        setCenterSearch(selected?.nombre || '');
      }
    } catch {
      if (requestId === centerRequest.current) setCenterError(true);
    } finally {
      if (requestId === centerRequest.current) {
        centerBusy.current = false;
        setCenterLoading(false);
      }
    }
  };

  const cancelSearch = (key: string) => {
    const timer = searchTimers.current[key];
    if (timer) {
      clearTimeout(timer);
      delete searchTimers.current[key];
    }
  };

  const scheduleSearch = (key: string, search: string, action: () => Promise<void>) => {
    cancelSearch(key);
    searchTimers.current[key] = setTimeout(() => {
      delete searchTimers.current[key];
      void action().catch((error) => console.error(error));
    }, 300);
    if (key === 'zones') setZoneSearch(search);
    if (key === 'supercenters') setSupercenterSearch(search);
    if (key === 'centers') setCenterSearch(search);
  };

  const handleChangeRol = (data: SelectOption) => {
    const selectedRole = selectOptionsRoles.find((option) => option.id === data.id);
    // Global roles do not have a zone, supercenter or center scope.
    if (selectedRole && !selectedRole.scoped) {
      cancelSearch('zones');
      cancelSearch('supercenters');
      cancelSearch('centers');
      zoneRequest.current += 1;
      supercenterRequest.current += 1;
      invalidateCenters();
      setEditable(false);
      setEditableSupercenter(false);
      setEditableCenter(false);
      setSelectedZone(null);
      setSelectedCenter(null);
      setSelectedSupercenter(null);
      setZoneSearch('');
      setSupercenterSearch('');
      setCenterSearch('');
    } else {
      setEditable(true);
    }
    if (selectedRole) setRole(selectedRole);
  };

  const resetSupercentersAndCenters = () => {
    cancelSearch('supercenters');
    cancelSearch('centers');
    supercenterRequest.current += 1;
    invalidateCenters();
    setSelectedSupercenter(null);
    setSelectedCenter(null);
    setSupercenterSearch('');
    setCenterSearch('');
    setOptionsSupercenter([]);
    setOptionsCenter([]);
    setSupercenterPage(0);
    setCenterPage(0);
    setSupercenterHasNextPage(false);
    setCenterHasNextPage(false);
    setEditableSupercenter(false);
    setEditableCenter(false);
  };

  const handleChangeZone = (option: RemoteMasterOption | null) => {
    cancelSearch('zones');
    setSelectedZone(option);
    setZoneSearch(option?.nombre || '');
    resetSupercentersAndCenters();
    setEditableSupercenter(Boolean(option));
    if (option) {
      void loadSupercenters(option.code, '', 0, false).catch((error) => console.error(error));
    }
  };

  const handleChangeSupercenter = (option: RemoteMasterOption | null) => {
    cancelSearch('supercenters');
    cancelSearch('centers');
    invalidateCenters();
    setSelectedSupercenter(option);
    setSupercenterSearch(option?.nombre || '');
    setSelectedCenter(null);
    setCenterSearch('');
    setOptionsCenter([]);
    setCenterPage(0);
    setCenterHasNextPage(false);
    setEditableCenter(Boolean(option));
    if (option) {
      void loadCenters(option.code, '', 0, false).catch((error) => console.error(error));
    }
  };

  const handleChangeCenter = (option: RemoteMasterOption | null) => {
    cancelSearch('centers');
    invalidateCenters();
    setSelectedCenter(option);
    setCenterSearch(option?.nombre || '');
  };

  const handleZoneInputChange = (value: string, reason: string) => {
    if (reason === 'reset') return;
    if (reason === 'input' || reason === 'clear') {
      setSelectedZone(null);
      resetSupercentersAndCenters();
      setOptionsZone([]);
      setZonePage(0);
      setZoneHasNextPage(false);
      setEditableSupercenter(false);
      scheduleSearch('zones', value, () => loadZones(value, 0, false));
      return;
    }
    setZoneSearch(value);
  };

  const handleSupercenterInputChange = (value: string, reason: string) => {
    if (reason === 'reset') return;
    if (reason === 'input' || reason === 'clear') {
      const zoneCode = selectedZone?.code;
      setSelectedSupercenter(null);
      invalidateCenters();
      setSelectedCenter(null);
      setCenterSearch('');
      setOptionsCenter([]);
      setCenterPage(0);
      setCenterHasNextPage(false);
      setEditableCenter(false);
      setOptionsSupercenter([]);
      setSupercenterPage(0);
      setSupercenterHasNextPage(false);
      if (zoneCode) {
        scheduleSearch('supercenters', value, () => loadSupercenters(zoneCode, value, 0, false));
      } else {
        setSupercenterSearch(value);
      }
      return;
    }
    setSupercenterSearch(value);
  };

  const handleCenterInputChange = (value: string, reason: string) => {
    if (reason === 'reset') return;
    if (reason === 'input' || reason === 'clear') {
      const supercenterCode = selectedSupercenter?.code;
      invalidateCenters();
      setSelectedCenter(null);
      setOptionsCenter([]);
      setCenterPage(0);
      setCenterHasNextPage(false);
      if (supercenterCode) {
        centerBusy.current = true;
        setCenterLoading(true);
        scheduleSearch('centers', value, () => loadCenters(supercenterCode, value, 0, false));
      } else {
        setCenterSearch(value);
      }
      return;
    }
    setCenterSearch(value);
  };

  useEffect(() => {
    let mounted = true;
    const initialize = async () => {
      setLoading(true);
      setZoneLoading(true);
      setInitializationError(false);
      try {
        const project = adminContext.projectToAdd;
        const zonesPage = await api.getZonesPage('', 0, 50, project?.codEmpresa || '0008');
        let zones = zonesPage.results;
        if (hasProject && project?.codZona && !zones.some((zone) => zone.code === project.codZona)) {
          const selectedZonePage = await api.getZonesPage(project.codZona, 0, 50, project.codEmpresa || '0008');
          zones = mergeOptions(zones, selectedZonePage.results);
        }
        if (!mounted) return;

        setOptionsZone(zones);
        setZonePage(zonesPage.page);
        setZoneHasNextPage(zonesPage.hasNextPage);
        const defaultZone = hasProject && project?.codZona
          ? zones.find((zone) => zone.code === project.codZona) || null
          : null;
        setSelectedZone(defaultZone);
        setZoneSearch(defaultZone?.nombre || '');
        setEditableSupercenter(Boolean(defaultZone));

        if (!defaultZone) {
          if (hasProject) throw new Error('Project zone was not found');
          return;
        }

        setSupercenterLoading(true);
        const supercentersPage = await api.getSupercentersPage(defaultZone.code, '', 0);
        let supercenters = supercentersPage.results;
        if (project?.codSupercentro && !supercenters.some((supercenter) => supercenter.code === project.codSupercentro)) {
          const selectedSupercenterPage = await api.getSupercentersPage(defaultZone.code, project.codSupercentro, 0);
          supercenters = mergeOptions(supercenters, selectedSupercenterPage.results);
        }
        if (!mounted) return;
        setOptionsSupercenter(supercenters);
        setSupercenterPage(supercentersPage.page);
        setSupercenterHasNextPage(supercentersPage.hasNextPage);
        const defaultSupercenter = project?.codSupercentro
          ? supercenters.find((supercenter) => supercenter.code === project.codSupercentro) || null
          : null;
        setSelectedSupercenter(defaultSupercenter);
        setSupercenterSearch(defaultSupercenter?.nombre || '');
        setEditableCenter(Boolean(defaultSupercenter));

        if (!defaultSupercenter) throw new Error('Project supercenter was not found');

        await loadCenters(defaultSupercenter.code, '', 0, false, project?.codCentro);
      } catch {
        if (mounted) setInitializationError(true);
      } finally {
        if (mounted) {
          setZoneLoading(false);
          setSupercenterLoading(false);
          setCenterLoading(false);
          setLoading(false);
        }
      }
    };

    void initialize();
    return () => {
      mounted = false;
      zoneRequest.current += 1;
      supercenterRequest.current += 1;
      centerRequest.current += 1;
      Object.values(searchTimers.current).forEach((timer) => clearTimeout(timer));
    };
  }, [initializationAttempt]);

  const showOptions = selectOptionsRoles.find((option) => option.id === role?.id)?.scoped ?? true;

  const loadNextZones = () => {
    if (zoneHasNextPage && !zoneLoading) {
      void loadZones(zoneSearch, zonePage + 1, true).catch((error) => console.error(error));
    }
  };

  const loadNextSupercenters = () => {
    if (selectedZone && supercenterHasNextPage && !supercenterLoading) {
      void loadSupercenters(selectedZone.code, supercenterSearch, supercenterPage + 1, true)
        .catch((error) => console.error(error));
    }
  };

  const loadNextCenters = () => {
    if (selectedSupercenter && centerHasNextPage && !centerBusy.current && !centerError) {
      void loadCenters(selectedSupercenter.code, centerSearch, centerPage + 1, true)
        .catch((error) => console.error(error));
    }
  };

  const saveRole = async (
    id: string | undefined,
    email: string | undefined,
    rol: string | undefined,
    codCentro: string,
    codSupercentro: string,
    codZona: string,
    codEmpresa: string
  ) => {
    setSaving(true);
    const data = id ? await api.editRole(id, email, rol, codCentro, codSupercentro, codZona, codEmpresa) :
      await api.addRole(id, email, rol, codCentro, codSupercentro, codZona, codEmpresa);
    if (data.error) {
      setOpenError(true);
      setSaving(false);
    } else {
      if (
        (adminContext.projectToAdd?.codCentro === codCentro
        || adminContext.projectToAdd?.codSupercentro === codSupercentro && !codCentro
        || adminContext.projectToAdd?.codZona === codZona && !codSupercentro && !codCentro)
      ){
        const cachedUsersPerProject = adminContext.usersPerProject;
        const filterPerProject = (user: UsuarioProyecto) => user.email === email;
        
        const currentUserPerProject = cachedUsersPerProject.find(filterPerProject);
        
        //const rol = data.rol;
        if(currentUserPerProject){ 
          const scope = codCentro ? 'project' : (codSupercentro ? 'supercenter' : (codZona && 'zone'));  
          const indexOfUserPerProject = cachedUsersPerProject.findIndex(filterPerProject);       
          const updatedUserPerProject = {
            idPermiso: data.permission?.id || currentUserPerProject.idPermiso,
            email: currentUserPerProject.email,
            rol: rol!,
            alcance: scope,
            provenance: data.permission?.provenance || 'MANUAL_GRANT',
            mutable: data.permission?.mutable ?? true,
          };
          const updatedUsersPerProject = [...cachedUsersPerProject.slice(0,indexOfUserPerProject),updatedUserPerProject,...cachedUsersPerProject.slice(indexOfUserPerProject+1)];
          adminContext.handleChangeUsersPerProject(updatedUsersPerProject);
        } else{
          const scope = codCentro ? 'project' : (codSupercentro ? 'supercenter' : (codZona && 'zone'));
          const updatedUserPerProject = {
            idPermiso: data.permission?.id,
            email: email!,
            rol: rol!,
            alcance: scope,
            provenance: data.permission?.provenance || 'MANUAL_GRANT',
            mutable: data.permission?.mutable ?? true,
          };
          const updatedUsersPerProject = [...cachedUsersPerProject,updatedUserPerProject];
          adminContext.handleChangeUsersPerProject(updatedUsersPerProject);
        }        
      }else{
        const updatedUsersPerProject = [...adminContext.usersPerProject].filter((user) => user.email !== email);
        adminContext.handleChangeUsersPerProject(updatedUsersPerProject);
      }

      const cachedUsers = Array.from(adminContext.allUsers);
      const filter = (user: Usuario) => user.email === email;
      const currentUser = cachedUsers.find(filter);
      if(currentUser){
        const indexOfUser = cachedUsers.findIndex(filter);
        const grupos = data.groups;
        const updatedUser = {
          id: currentUser.id,
          email: currentUser.email,
          username: currentUser.username ,
          nombre: currentUser.nombre ,
          grupos,
          permisos: currentUser.permisos
        }; 
        const updatedUsers = [...cachedUsers.slice(0,indexOfUser),updatedUser,...cachedUsers.slice(indexOfUser + 1)];
        adminContext.handleSetAllUsers(updatedUsers);
      }
      adminContext.handleChangeUpdatedPermissions({}); // Removing cached permissions from context
      adminContext.handleRefreshUsers();
      setSaving(false);
      adminContext.handleSetIsEdition(false);
    }
  };

  const checkRole = (id?: number) => selectOptionsRoles.find((option) => option.id === id)?.role;

  const renderErrorSaving = () => {
    return <Dialog
      classes={{
        paper: styles.dialogError
      }}
      BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.15)' } }}
      PaperProps={{ sx: { borderRadius: '20px', minWidth: '700px' } }}
      open={openError}
      onClose={() => setOpenError(false)}
    >
      <div className={styles.dialogContentError}>
        <div data-testid='error-test' className={styles.serverError}>
          <div className={styles.serverErrorContent}>
            <SvgIcon component={error} inheritViewBox />
            <div id='error-title' className={styles.title}>{t('administration.errorTitle')}</div>
            <div id='error-subtitle' className={styles.subtitle}>{t('administration.errorDescription')}</div>
          </div>
        </div>
        <Stack spacing={2} direction="row" justifyContent="center">
          <Button className={styles.buttonConfirm} onClick={() => {
            setOpenError(false);
          }} disableRipple={true} variant="contained">{t('administration.acceptButton')}</Button>
        </Stack>
      </div>
    </Dialog>;
  };
  return (
    <DetailsViewProvider>
      <div data-testid="edit-test" className='editRoles'>
        <div>
          <Header>
            <TabMenuAdmin editRoles={true} />
          </Header>
          <div className={styles.editPanel}>
            {(loading || saving) && <LoadingSpinner />}
            {renderErrorSaving()}
            {!loading &&
              <div className={styles.editcontainer}>
                {initializationError && <div role="alert">
                  <span>{t('administration.edition.scopeLoadError')}</span>
                  <Button onClick={() => setInitializationAttempt(attempt => attempt + 1)}>
                    {t('administration.searchDialog.retry')}
                  </Button>
                </div>}
                <div className={styles.container}>
                  <div className={styles.selectSearch}>
                    <SimpleSelect
                      id='select-rol'
                      options={selectOptionsRoles}
                      value={role}
                      label={t('administration.role')}
                      handleChangeValue={handleChangeRol}
                      editable={true}
                      customClassName={styles.roleDropdown}
                    />
                  </div>
                </div>
                <div className={styles.container}>
                  <div className={styles.selectSearch}>
                    <SimpleSelect
                      id='select-enterprise'
                      options={selectOptionsEnterprise}
                      value={selectOptionsEnterprise[0]}
                      label={t('administration.enterprise')}
                      handleChangeValue={() => null}
                      editable={editable && showOptions}
                      customClassName={styles.roleDropdown}
                    />
                  </div>
                </div>
                <ul>
                  <li className={styles.zone}>
                    <div className={styles.container}>
                      <div className={styles.selectSearchZone}>
                        <RemotePaginatedSelect
                          id='select-zone'
                          options={optionsZone}
                          value={showOptions ? selectedZone : null}
                          inputValue={showOptions ? zoneSearch : selectAllOptions[0].nombre}
                          label={t('administration.zone')}
                          loading={zoneLoading}
                          hasNextPage={zoneHasNextPage}
                          editable={editable && showOptions}
                          onChange={handleChangeZone}
                          onInputChange={handleZoneInputChange}
                          onLoadMore={loadNextZones}
                        />
                      </div>
                    </div>
                  </li>
                  <li className={styles.supercenter}>
                    <div className={styles.container}>
                      <div className={styles.selectSearchSupercenter}>
                        <RemotePaginatedSelect
                          id='select-supercenter'
                          options={optionsSupercenter}
                          value={showOptions ? selectedSupercenter : null}
                          inputValue={showOptions ? (supercenterSearch || (editableSupercenter ? '' : '-')) : selectAllOptions[1].nombre}
                          label={t('administration.supercenter')}
                          loading={supercenterLoading}
                          hasNextPage={supercenterHasNextPage}
                          editable={editableSupercenter && showOptions}
                          onChange={handleChangeSupercenter}
                          onInputChange={handleSupercenterInputChange}
                          onLoadMore={loadNextSupercenters}
                        />
                      </div>
                    </div>
                  </li>
                  <li className={styles.center}>
                    <div className={styles.container}>
                      <div className={styles.selectSearchCenter}>
                        <RemotePaginatedSelect
                          error={centerError}
                          onRetry={() => { if (!centerBusy.current) void centerRetry.current?.(); }}
                          id='select-center'
                          options={optionsCenter}
                          value={showOptions ? selectedCenter : null}
                          inputValue={showOptions ? (centerSearch || (editableCenter ? '' : '-')) : selectAllOptions[2].nombre}
                          label={t('administration.center')}
                          loading={centerLoading}
                          hasNextPage={centerHasNextPage}
                          editable={editableCenter && showOptions}
                          onChange={handleChangeCenter}
                          onInputChange={handleCenterInputChange}
                          onLoadMore={loadNextCenters}
                        />
                      </div>
                    </div>
                  </li>
                </ul>
                <Stack spacing={2} direction="row" justifyContent="right" marginTop={'3.5em'}>
                  <Button className={styles.buttonSave}
                    disabled={saving || initializationError || (showOptions && (centerLoading || centerError))}
                    onClick={() => {
                      saveRole(
                        adminContext.permissionToEdit?.id,
                        adminContext.userToEdit?.email,
                        checkRole(role?.id),
                        selectedCenter?.code || '',
                        selectedSupercenter?.code || '',
                        selectedZone?.code || '',
                        '0008');
                    }}
                    disableRipple={true}
                    variant="contained">{t('save')}</Button>
                </Stack>
              </div>}
          </div>
        </div>
      </div>
    </DetailsViewProvider>
  );
};

export default EditRoles;
